import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, Sparkles, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { studioBranding } from "@/lib/studioBranding";
import { useDocumentBranding } from "@/lib/useDocumentBranding";

export default function StudioLayout({ tenant, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const brand = studioBranding(tenant?.slug);
  // Any logo (uploaded or bundled) already carries the studio's identity, so we
  // show it larger and hide the duplicate text name.
  const logoSrc = tenant?.logo_url || brand?.logoUrl || brand?.iconUrl || null;
  useDocumentBranding(tenant, "Studio Console");

  const navItem = ({ isActive }) =>
    `px-3 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors ${isActive ? "text-white" : "text-white/50 hover:text-white"}`;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white font-mono-ui" data-testid="studio-layout">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/studio" className="flex items-center gap-3" data-testid="studio-brand-link">
              {logoSrc ? (
                <img
                  src={logoSrc}
                  alt={tenant?.name}
                  className="h-11 md:h-12 w-auto max-w-[240px] object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <>
                  <Sparkles className="w-5 h-5" style={{ color: tenant?.theme_color || "#7C3AED" }} />
                  <span className="font-display font-bold tracking-tight text-base">{tenant?.name || "Studio"}</span>
                </>
              )}
              <span className="text-white/30 text-xs uppercase tracking-[0.3em] ml-2 hidden sm:inline">Studio Console</span>
            </Link>
            <nav className="flex items-center gap-1">
              <NavLink to="/studio" end className={navItem} data-testid="studio-nav-branding">Branding</NavLink>
              <NavLink to="/studio/catalogue" className={navItem} data-testid="studio-nav-catalogue">Catalogue</NavLink>
              <NavLink to="/studio/plans" className={navItem} data-testid="studio-nav-plans">Plans</NavLink>
              <NavLink to="/studio/subscribers" className={navItem} data-testid="studio-nav-subscribers">Subscribers</NavLink>
              <NavLink to="/studio/billing" className={navItem} data-testid="studio-nav-billing">Billing</NavLink>
              <NavLink to="/studio/analytics" className={navItem} data-testid="studio-nav-analytics">Analytics</NavLink>
              <NavLink to="/studio/revenue" className={navItem} data-testid="studio-nav-revenue">Revenue</NavLink>
              <NavLink to="/studio/domains" className={navItem} data-testid="studio-nav-domains">Domains</NavLink>
            </nav>
          </div>
          <div className="flex items-center gap-3">
            {tenant?.slug && (
              <Link
                to={`/s/${tenant.slug}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 uppercase tracking-widest"
                data-testid="studio-view-storefront-link"
              >
                View storefront <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            <span className="text-xs text-white/40 hidden md:inline" data-testid="studio-user-email">
              {user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white rounded-none h-8"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="studio-logout-button"
            >
              <LogOut className="w-3.5 h-3.5 mr-1.5" />
              <span className="text-xs uppercase tracking-widest">Sign out</span>
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 py-8">{children}</main>
    </div>
  );
}
